/*eslint max-lines-per-function: ["error", 195]*/
import React from 'react';
import type { TFunction } from 'i18next';

import PieChartProgressBar from '../../../app/components/PieChartProgressBar';
import { GridRecord } from '../../../common/components/Grid/GridDataTypes';
import numberFormatter from '../../../common/numberFormatter';
import {
  GetDictionaryDto,
  GetEstimateItemDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import mathUtils from '../../../utils/mathUtils';
import { TKeys } from '../../../translations';

export const getItemsGridData = (
  items: GetEstimateItemDto[],
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  lang: string,
) => {
  const data: GridRecord[] = [];

  const getDictionaryPlEnValue = (dictionary: GetDictionaryDto) =>
    lang === 'PL' ? dictionary?.value : dictionary?.valueEn;

  for (const d of items) {
    const purchaseAmountToAccept = mathUtils.subtract(
      mathUtils.round(d.purchaseNetTotal),
      mathUtils.round(d.purchaseInvoicesSum),
    );
    const salesAmountToInvoice = mathUtils.subtract(
      mathUtils.round(d.salesNetTotal),
      mathUtils.round(d.salesInvoicesSum),
    );
    data.push({
      key: d.id,
      values: {
        positionName: d.position.name,
        positionCountry: d.countryDictionary
          ? getDictionaryPlEnValue(d.countryDictionary)
          : '',
        'pathPhaseDictionary.value': d.pathPhaseDictionary
          ? getDictionaryPlEnValue(d.pathPhaseDictionary)
          : '',
        communicationChannel: d.position.dictionary
          ? getDictionaryPlEnValue(d.position.dictionary)
          : '',
        deliveryTeam: (d.deliveryTeam ?? d.specialistTeam)?.name,
        rcSalesTotal: numberFormatter(d.rcSalesTotal, true),
        salesNetTotal: numberFormatter(d.salesNetTotal, true),
        salesNet: numberFormatter(d.salesNet, true),
        salesCommissionAmount: numberFormatter(d.salesCommissionAmount, true),
        salesDiscountPercent: d.salesDiscountPercent,
        salesSurchargePercent: d.salesSurchargePercent,
        salesNetTechnicalCost: numberFormatter(d.salesNetTechnicalCost, true),
        salesBeerTax: numberFormatter(d.salesBeerTax, true),
        salesPrintingCosts: numberFormatter(d.salesPrintingCosts, true),
        salesOperatingFee: numberFormatter(d.salesOperatingFee, true),
        salesBlockSurcharge: numberFormatter(d.salesBlockSurcharge, true),
        salesTransferCosts: numberFormatter(d.salesTransferCosts, true),
        rcPurchase: numberFormatter(d.rcPurchase, true),
        purchaseNetTotal: numberFormatter(d.purchaseNetTotal, true),
        purchaseNet: numberFormatter(d.purchaseNet, true),
        purchaseTechnicalCost: numberFormatter(d.purchaseTechnicalCost, true),
        purchaseBeerTax: numberFormatter(d.purchaseBeerTax, true),
        purchasePrintingCosts: numberFormatter(d.purchasePrintingCosts, true),
        purchaseOperatingFee: numberFormatter(d.purchaseOperatingFee, true),
        purchaseBlockSurcharge: numberFormatter(d.purchaseBlockSurcharge, true),
        purchaseTransferCosts: numberFormatter(d.purchaseTransferCosts, true),
        purchaseDiscountPercent: d.purchaseDiscountPercent,
        purchaseSurchargePercent: d.purchaseSurchargePercent,
        profileCategory: d.profileCategory?.name,
        additionalDescription: d.additionalDescription,
        categoryInternet: getDictionaryPlEnValue(d.categoryInternet),
        isUpSell: d.isUpSell ? t(tk.common.yes) : t(tk.common.no),
        netCustomerAccount: d.netCustomerAccount,
        numberOfMedia: d.numberOfMedia,
        orderNumber: d.orderNumber,
        ownName: d.ownName,
        technologyDictionary: getDictionaryPlEnValue(d.technologyDictionary),
        regionDictionary: getDictionaryPlEnValue(d.regionDictionary),
        formatRadio: getDictionaryPlEnValue(d.formatRadio),
        formatPress: getDictionaryPlEnValue(d.formatPress),
        formatInternet: getDictionaryPlEnValue(d.formatInternet),
        emissionTypeRadio: getDictionaryPlEnValue(d.emissionTypeRadio),
        emissionTypeCinema: getDictionaryPlEnValue(d.emissionTypeCinema),
        purchaseFormInternet: getDictionaryPlEnValue(d.purchaseFormInternet),
        categoryLabcon: getDictionaryPlEnValue(d.categoryLabcon),
        copySize: getDictionaryPlEnValue(d.copySize),
        purchaseMethod: getDictionaryPlEnValue(d.purchaseMethod),
        medium: getDictionaryPlEnValue(d.medium),
        labconChannel: getDictionaryPlEnValue(d.labconChannel),
        service: d.service,
        action: d.action,
        actionGroup: d.actionGroup,
        salesAmountToInvoice: numberFormatter(salesAmountToInvoice, true),
        purchaseAmountToAccept: numberFormatter(purchaseAmountToAccept, true),
        salesAmountToInvoicePB: (
          <PieChartProgressBar
            currentVal={mathUtils.round(d.salesInvoicesSum)}
            text={t(tk.estimates.invoiced)}
            maxVal={mathUtils.round(d.salesNetTotal)}
            width={20}
            height={20}
          />
        ),
        purchaseAmountToAcceptPB: (
          <PieChartProgressBar
            currentVal={mathUtils.round(d.purchaseInvoicesSum)}
            text={t(tk.estimates.accepted)}
            maxVal={mathUtils.round(d.purchaseNetTotal)}
            width={20}
            height={20}
          />
        ),
      },
      orderByNumberValues: {
        rcSalesTotal: mathUtils.parse(d.rcSalesTotal),
        salesNetTotal: mathUtils.parse(d.salesNetTotal),
        salesNet: mathUtils.parse(d.salesNet),
        salesCommissionAmount: mathUtils.parse(d.salesCommissionAmount),
        salesDiscountPercent: mathUtils.parse(d.salesDiscountPercent),
        salesSurchargePercent: mathUtils.parse(d.salesSurchargePercent),
        salesNetTechnicalCost: mathUtils.parse(d.salesNetTechnicalCost),
        salesBeerTax: mathUtils.parse(d.salesBeerTax),
        salesPrintingCosts: mathUtils.parse(d.salesPrintingCosts),
        salesOperatingFee: mathUtils.parse(d.salesOperatingFee),
        salesBlockSurcharge: mathUtils.parse(d.salesBlockSurcharge),
        salesTransferCosts: mathUtils.parse(d.salesTransferCosts),
        rcPurchase: mathUtils.parse(d.rcPurchase),
        purchaseNetTotal: mathUtils.parse(d.purchaseNetTotal),
        purchaseNet: mathUtils.parse(d.purchaseNet),
        purchaseTechnicalCost: mathUtils.parse(d.purchaseTechnicalCost),
        purchaseBeerTax: mathUtils.parse(d.purchaseBeerTax),
        purchasePrintingCosts: mathUtils.parse(d.purchasePrintingCosts),
        purchaseOperatingFee: mathUtils.parse(d.purchaseOperatingFee),
        purchaseBlockSurcharge: mathUtils.parse(d.purchaseBlockSurcharge),
        purchaseTransferCosts: mathUtils.parse(d.purchaseTransferCosts),
        purchaseDiscountPercent: mathUtils.parse(d.purchaseDiscountPercent),
        purchaseSurchargePercent: mathUtils.parse(d.purchaseSurchargePercent),
        salesAmountToInvoice: mathUtils.parse(salesAmountToInvoice),
        purchaseAmountToAccept: mathUtils.parse(purchaseAmountToAccept),
      },
      tdCssClass: {
        rcSalesTotal: 'amounts-align-right',
        salesNetTotal: 'amounts-align-right',
        salesNet: 'amounts-align-right',
        salesCommissionAmount: 'amounts-align-right',
        salesNetTechnicalCost: 'amounts-align-right',
        salesBeerTax: 'amounts-align-right',
        salesPrintingCosts: 'amounts-align-right',
        salesOperatingFee: 'amounts-align-right',
        salesBlockSurcharge: 'amounts-align-right',
        salesTransferCosts: 'amounts-align-right',
        rcPurchase: 'amounts-align-right',
        purchaseNetTotal: 'amounts-align-right',
        purchaseNet: 'amounts-align-right',
        purchaseTechnicalCost: 'amounts-align-right',
        purchaseBeerTax: 'amounts-align-right',
        purchasePrintingCosts: 'amounts-align-right',
        purchaseOperatingFee: 'amounts-align-right',
        purchaseBlockSurcharge: 'amounts-align-right',
        purchaseTransferCosts: 'amounts-align-right',
        salesAmountToInvoice: 'amounts-align-right',
        purchaseAmountToAccept: 'amounts-align-right',
        salesAmountToInvoicePB: 'amounts-align-center',
        purchaseAmountToAcceptPB: 'amounts-align-center',
      },
    });
  }
  return data;
};

export const getSummary = (
  data: GetEstimateItemDto[],
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  keys: string[],
) => {
  let rcSalesTotal = 0;
  let salesNetTotal = 0;
  let salesNet = 0;
  let salesCommissionAmount = 0;
  let salesNetTechnicalCost = 0;
  let salesBeerTax = 0;
  let salesPrintingCosts = 0;
  let salesOperatingFee = 0;
  let salesBlockSurcharge = 0;
  let salesTransferCosts = 0;
  let rcPurchase = 0;
  let purchaseNetTotal = 0;
  let purchaseNet = 0;
  let purchaseTechnicalCost = 0;
  let purchaseBeerTax = 0;
  let purchasePrintingCosts = 0;
  let purchaseOperatingFee = 0;
  let purchaseBlockSurcharge = 0;
  let purchaseTransferCosts = 0;
  let salesAmountToInvoice = 0;
  let purchaseAmountToAccept = 0;
  let salesInvoicesSum = 0;
  let purchaseInvoicesSum = 0;

  data = data.filter(item => keys.includes(item.id));

  data.map(rec => {
    rcSalesTotal = mathUtils.add(
      rcSalesTotal,
      mathUtils.round(rec.rcSalesTotal),
    );
    salesNetTotal = mathUtils.add(
      salesNetTotal,
      mathUtils.round(rec.salesNetTotal),
    );
    salesNet = mathUtils.add(salesNet, mathUtils.round(rec.salesNet));
    salesCommissionAmount = mathUtils.add(
      salesCommissionAmount,
      mathUtils.round(rec.salesCommissionAmount),
    );
    salesNetTechnicalCost = mathUtils.add(
      salesNetTechnicalCost,
      mathUtils.round(rec.salesNetTechnicalCost),
    );
    salesBeerTax = mathUtils.add(
      salesBeerTax,
      mathUtils.round(rec.salesBeerTax),
    );
    salesPrintingCosts = mathUtils.add(
      salesPrintingCosts,
      mathUtils.round(rec.salesPrintingCosts),
    );
    salesOperatingFee = mathUtils.add(
      salesOperatingFee,
      mathUtils.round(rec.salesOperatingFee),
    );
    salesBlockSurcharge = mathUtils.add(
      salesBlockSurcharge,
      mathUtils.round(rec.salesBlockSurcharge),
    );
    salesTransferCosts = mathUtils.add(
      salesTransferCosts,
      mathUtils.round(rec.salesTransferCosts),
    );
    rcPurchase = mathUtils.add(rcPurchase, mathUtils.round(rec.rcPurchase));
    purchaseNetTotal = mathUtils.add(
      purchaseNetTotal,
      mathUtils.round(rec.purchaseNetTotal),
    );
    purchaseNet = mathUtils.add(purchaseNet, mathUtils.round(rec.purchaseNet));
    purchaseTechnicalCost = mathUtils.add(
      purchaseTechnicalCost,
      mathUtils.round(rec.purchaseTechnicalCost),
    );
    purchaseBeerTax = mathUtils.add(
      purchaseBeerTax,
      mathUtils.round(rec.purchaseBeerTax),
    );
    purchasePrintingCosts = mathUtils.add(
      purchasePrintingCosts,
      mathUtils.round(rec.purchasePrintingCosts),
    );
    purchaseOperatingFee = mathUtils.add(
      purchaseOperatingFee,
      mathUtils.round(rec.purchaseOperatingFee),
    );
    purchaseBlockSurcharge = mathUtils.add(
      purchaseBlockSurcharge,
      mathUtils.round(rec.purchaseBlockSurcharge),
    );
    purchaseTransferCosts = mathUtils.add(
      purchaseTransferCosts,
      mathUtils.round(rec.purchaseTransferCosts),
    );
    salesAmountToInvoice = mathUtils.add(
      salesAmountToInvoice,
      mathUtils.subtract(
        mathUtils.round(rec.salesNetTotal),
        mathUtils.round(rec.salesInvoicesSum),
      ),
    );
    purchaseAmountToAccept = mathUtils.add(
      purchaseAmountToAccept,
      mathUtils.subtract(
        mathUtils.round(rec.purchaseNetTotal),
        mathUtils.round(rec.purchaseInvoicesSum),
      ),
    );
    salesInvoicesSum = mathUtils.add(
      salesInvoicesSum,
      mathUtils.round(rec.salesInvoicesSum),
    );
    purchaseInvoicesSum = mathUtils.add(
      purchaseInvoicesSum,
      mathUtils.round(rec.purchaseInvoicesSum),
    );
  });
  return {
    key: 'summary',
    values: {
      positionName: t(tk.common.sumUpperCase),
      rcSalesTotal: numberFormatter(rcSalesTotal, true),
      salesNetTotal: numberFormatter(salesNetTotal, true),
      salesNet: numberFormatter(salesNet, true),
      salesCommissionAmount: numberFormatter(salesCommissionAmount, true),
      salesNetTechnicalCost: numberFormatter(salesNetTechnicalCost, true),
      salesBeerTax: numberFormatter(salesBeerTax, true),
      salesPrintingCosts: numberFormatter(salesPrintingCosts, true),
      salesOperatingFee: numberFormatter(salesOperatingFee, true),
      salesBlockSurcharge: numberFormatter(salesBlockSurcharge, true),
      salesTransferCosts: numberFormatter(salesTransferCosts, true),
      rcPurchase: numberFormatter(rcPurchase, true),
      purchaseNetTotal: numberFormatter(purchaseNetTotal, true),
      purchaseNet: numberFormatter(purchaseNet, true),
      purchaseTechnicalCost: numberFormatter(purchaseTechnicalCost, true),
      purchaseBeerTax: numberFormatter(purchaseBeerTax, true),
      purchasePrintingCosts: numberFormatter(purchasePrintingCosts, true),
      purchaseOperatingFee: numberFormatter(purchaseOperatingFee, true),
      purchaseBlockSurcharge: numberFormatter(purchaseBlockSurcharge, true),
      purchaseTransferCosts: numberFormatter(purchaseTransferCosts, true),
      salesAmountToInvoice: numberFormatter(salesAmountToInvoice, true),
      purchaseAmountToAccept: numberFormatter(purchaseAmountToAccept, true),
      salesAmountToInvoicePB: (
        <PieChartProgressBar
          currentVal={salesInvoicesSum}
          text={t(tk.estimates.invoiced)}
          maxVal={salesNetTotal}
          width={20}
          height={20}
        />
      ),
      purchaseAmountToAcceptPB: (
        <PieChartProgressBar
          currentVal={purchaseInvoicesSum}
          text={t(tk.estimates.accepted)}
          maxVal={purchaseNetTotal}
          width={20}
          height={20}
        />
      ),
    },
    tdCssClass: {
      rcSalesTotal: 'amounts-align-right',
      salesNetTotal: 'amounts-align-right',
      salesNet: 'amounts-align-right',
      salesCommissionAmount: 'amounts-align-right',
      salesNetTechnicalCost: 'amounts-align-right',
      salesBeerTax: 'amounts-align-right',
      salesPrintingCosts: 'amounts-align-right',
      salesOperatingFee: 'amounts-align-right',
      salesBlockSurcharge: 'amounts-align-right',
      salesTransferCosts: 'amounts-align-right',
      rcPurchase: 'amounts-align-right',
      purchaseNetTotal: 'amounts-align-right',
      purchaseNet: 'amounts-align-right',
      purchaseTechnicalCost: 'amounts-align-right',
      purchaseBeerTax: 'amounts-align-right',
      purchasePrintingCosts: 'amounts-align-right',
      purchaseOperatingFee: 'amounts-align-right',
      purchaseBlockSurcharge: 'amounts-align-right',
      purchaseTransferCosts: 'amounts-align-right',
      salesAmountToInvoice: 'amounts-align-right',
      purchaseAmountToAccept: 'amounts-align-right',
      salesAmountToInvoicePB: 'amounts-align-center',
      purchaseAmountToAcceptPB: 'amounts-align-center',
    },
  };
};
